/**
 * PostHog Cloud EU — analytics wrapper.
 * Production-only: all functions are no-ops in dev (logs to console instead).
 * RGPD: person_profiles='identified_only', autocapture disabled.
 *
 * posthog-js is NOT statically imported — it is lazy-loaded inside initPostHog()
 * so the 61kB gz chunk is deferred from the initial bundle.
 * Events that fire before init are queued and flushed once posthog is ready.
 *
 * Phase 4 — Complete event dictionary (C.5 telemetry audit):
 *
 * Tour events:
 *   tour_started         { tour: 'player' | 'playlist' }
 *   tour_step            { tour, step: number, action?: string }
 *   tour_completed       { tour, atStep: number }
 *   tour_skipped         { tour, atStep: number }
 *   welcome_modal_shown
 *   welcome_modal_dismissed  { method: 'cta' | 'esc' | 'overlay' }
 *
 * Playlist like events (useLikedPlaylists — A.0):
 *   playlist_liked       { playlistId, source }
 *   playlist_unliked     { playlistId, source }
 *     sources: 'sidebar-suggestion' | 'playlist-detail' | 'undo' | 'library-panel' | 'sidebar-context'
 *
 * Playlist management:
 *   playlist_create_cta_clicked  { source: 'sidebar-top' | 'library-panel' }
 *   playlist_created             { playlistId, name, trackCount }
 *
 * Discovery:
 *   playlist_discover_filter_apply  { axes, values }
 *
 * Migration (A.3):
 *   migration_v2_applied   { businessType, seededCount }
 *   migration_v2_skipped   { reason, businessType? }
 *
 * Smart shuffle (Phase 3 legacy):
 *   smart_shuffle_used   { playlistLength, variant, businessType }
 */

let _posthog = null;
let initialized = false;
// Queue of [eventName, properties] pairs captured before init
const _queue = [];

export async function initPostHog() {
  if (initialized) return;
  if (import.meta.env.MODE !== 'production') {
    // Dev: log capture-events to console instead of sending
    window.__posthog_dev = true;
    initialized = true;
    // Flush dev queue to console
    _queue.forEach(([name, props]) => console.log('[posthog:dev]', name, props));
    _queue.length = 0;
    return;
  }
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const host = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';
  if (!apiKey) {
    console.warn('[posthog] VITE_POSTHOG_KEY not set — analytics disabled');
    return;
  }
  // Lazy-load posthog-js — keeps it out of the initial bundle
  const { default: posthog } = await import('posthog-js');
  posthog.init(apiKey, {
    api_host: host,
    person_profiles: 'identified_only', // RGPD: no anonymous profiles
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: false, // disabled: we control all events explicitly
    loaded: () => {
      if (import.meta.env.DEV) console.log('[posthog] loaded');
    },
  });
  _posthog = posthog;
  initialized = true;
  // Flush queued events
  _queue.forEach(([name, props]) => {
    try { _posthog.capture(name, props); } catch { /* silent */ }
  });
  _queue.length = 0;
}

export function capture(eventName, properties = {}) {
  if (window.__posthog_dev) {
    console.log('[posthog:dev]', eventName, properties);
    return;
  }
  if (!initialized || !_posthog) {
    // Queue for flush after init
    _queue.push([eventName, properties]);
    return;
  }
  try {
    _posthog.capture(eventName, properties);
  } catch (e) {
    console.warn('[posthog] capture failed:', e);
  }
}

export function identify(userId, traits = {}) {
  if (!initialized || !_posthog || window.__posthog_dev) return;
  try {
    _posthog.identify(userId, traits);
  } catch { /* silent */ }
}

export function reset() {
  if (!initialized || !_posthog || window.__posthog_dev) return;
  try { _posthog.reset(); } catch { /* silent */ }
}

export function isFeatureEnabled(flag) {
  if (!initialized || !_posthog || window.__posthog_dev) return false;
  try { return _posthog.isFeatureEnabled(flag); } catch { return false; }
}
